import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useDeleteClassLink, useGetMyClassGroups as useGetMyClassrooms, useLinkContent } from '@/api/gen/classroom/classroom.gen';
import { useJoinWithLocale } from '@/hooks';
import { useUser } from '@/lib/auth';
import { selectData } from '@/shared/utils';
import { ClassroomContentLinkedToast } from '../components/ClassroomContentLinkedToast';
import { type ContentShareType } from '../types';
import { classroomContentToken, type ClassroomSource, handleClassroomLinkError, invalidateClassroomMutationQueries, trackClassroomEvent } from '../utils';
interface UseClassroomLinkingParams {
  contentId: string;
  contentType: ContentShareType;
  source?: ClassroomSource;
}
const getErrorStatus = (error: unknown) => {
  if (error && typeof error === 'object' && 'status' in error) {
    return String(error.status);
  }
  return undefined;
};

/**
 * 내 클래스 목록 + content link 상태 + link/unlink mutation을 한 번에 제공.
 *
 * `useGetMyClassrooms` 에 `linkedContentId`/`linkedContentType` 를 전달하면 응답
 * 각 classroom 에 `isLinked`/`linkedClassLinkId` 가 채워져 오므로 별도 per-classroom
 * `getClassLinks` 호출 없이 1회 요청으로 link 상태를 알 수 있다.
 *
 * 한 모달에서 여러 classroom 에 대해 mutate 하므로 wrapper hook(classroomId 인자
 * 고정형) 대신 raw mutation 을 쓰되, invalidation 면은 mutation wrapper 와 같이
 * `invalidateClassroomMutationQueries` 로 통일한다.
 */
export function useClassroomLinking({
  contentId,
  contentType,
  source = 'classroom_link_modal'
}: UseClassroomLinkingParams) {
  const queryClient = useQueryClient();
  const contentToken = classroomContentToken(contentType);
  const {
    t
  } = useTranslation(['common']);
  const {
    isAuthenticated,
    isInitialized,
    user
  } = useUser();
  const canLoadClassrooms = isAuthenticated && user?.type?.toLowerCase() !== 'student';
  const {
    data: classrooms = [],
    isLoading: isLoadingClassrooms
  } = useGetMyClassrooms({
    sortBy: 'updatedAt',
    order: 'desc',
    linkedContentId: contentId,
    linkedContentType: contentType
  }, {
    query: {
      enabled: canLoadClassrooms,
      select: selectData
    }
  });
  const classroomPath = useJoinWithLocale();
  const linkMutation = useLinkContent({
    mutation: {
      onSuccess: (_data, variables) => {
        trackClassroomEvent(`success_link_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source
        });
        toast.custom(<ClassroomContentLinkedToast classroomId={variables.classGroupId} contentType={contentType} classroomPath={classroomPath} />, {
          duration: 2500,
          position: 'top-center'
        });
        return invalidateClassroomMutationQueries(queryClient, variables.classGroupId);
      },
      onError: (error, variables) => {
        trackClassroomEvent(`fail_link_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source,
          errorStatus: getErrorStatus(error)
        });
        void handleClassroomLinkError(error, key => t(key as any));
      }
    }
  });
  const unlinkMutation = useDeleteClassLink({
    mutation: {
      onSuccess: (_data, variables) => {
        trackClassroomEvent(`success_unlink_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source
        });
        return invalidateClassroomMutationQueries(queryClient, variables.classGroupId);
      },
      onError: (error, variables) => {
        trackClassroomEvent(`fail_unlink_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source,
          errorStatus: getErrorStatus(error)
        });
      }
    }
  });
  const linkClassroom = (classroomId: string) => {
    trackClassroomEvent(`click_link_classroom_${contentToken}`, {
      classroomId,
      contentId,
      source
    });
    linkMutation.mutate({
      classGroupId: classroomId,
      data: {
        contentId,
        contentType
      }
    });
  };
  const unlinkClassroom = (classroomId: string, classLinkId: string) => {
    trackClassroomEvent(`click_unlink_classroom_${contentToken}`, {
      classroomId,
      contentId,
      source
    });
    unlinkMutation.mutate({
      classGroupId: classroomId,
      classLinkId
    });
  };
  return {
    classrooms,
    isLoadingClassrooms,
    isInitialized,
    canLoadClassrooms,
    isPending: linkMutation.isPending || unlinkMutation.isPending,
    linkClassroom,
    unlinkClassroom
  };
}